import { useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import dayjs from 'dayjs'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import {
    createColumns,
    createInternalLinkFormatter,
    FilterItemConfig,
} from '@/components/ui/data-table-customized'
import { getEntityConfig } from '@/components/Form/Shared/SimpleAsyncSelect/config'
import { DEPARTMENT_IDS } from '@/constants/departments'
import { formatCurrency } from '@/utils/function'
import { generatePathDatacentral, paths } from '@/utils/path'
import { Link } from 'react-router-dom'

import { getEmployeeIncomes, getEmployeeIncomesStats } from '../services'
import { Income } from '../types'
import { EMPLOYEE_INCOME_EXCEL_COLUMNS } from '../constants/excelColumns'

interface IncomeFilters {
    sale_manager_codes: number[]
    gte: string
    lte: string
}

// Form type same as IncomeFilters (gte/lte used directly by DateRangePicker)
type IncomeFormFields = IncomeFilters

const today = dayjs()

const getDefaultGte = () =>
    today.get('date') <= 5
        ? today.subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
        : today.startOf('month').format('YYYY-MM-DD')

const getDefaultLte = () =>
    today.get('date') <= 5
        ? today.subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
        : today.endOf('month').format('YYYY-MM-DD')

export const useIncomeList = () => {
    const { t } = useTranslation('report')

    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        setFilters,
        searchInputProps,
    } = useUrlSearchParams<IncomeFilters>({
        defaults: {
            sort_by: 'invoice_item_amount',
            sort_order: 'desc',
        },
        filterDefaults: {
            sale_manager_codes: [],
            gte: getDefaultGte(),
            lte: getDefaultLte(),
        },
    })

    const { control } = useForm<IncomeFormFields>({
        defaultValues: filters,
    })

    // Data fetching
    const { data, renderState, isFetching, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.REPORTS_INCOMES, queryParams],
        queryFn: () => getEmployeeIncomes(queryParams),
        placeholderData: true,
    })

    // Stats fetching (placeholderData keeps previous data during refetch to avoid UI jump)
    const { data: statsData } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_INCOMES_STATS,
            queryParams.q,
            queryParams.filters,
        ],
        queryFn: () =>
            getEmployeeIncomesStats({
                q: queryParams.q,
                filters: queryParams.filters,
            }),
        placeholderData: true,
    })

    // Columns
    const columns = useMemo(
        () =>
            createColumns<Income>([
                {
                    key: 'customer_name',
                    title: t('customer'),
                    width: 250,
                    headerClassName: 'text-left',
                    cellClassName: 'truncate uppercase',
                    sortable: true,
                    formatter: createInternalLinkFormatter({
                        pathKey: 'customerDetail',
                        pathParams: (value) => ({ id: value }),
                        label: (_, row) =>
                            `${row.customer_id} - ${row.customer_name}`,
                    }),
                },
                {
                    key: 'invoice_id',
                    title: t('invoice_id'),
                    width: 100,
                    headerClassName: 'text-left',
                    render: (value) =>
                        value ? (
                            <Link
                                to={generatePathDatacentral(
                                    paths.invoiceDetail,
                                    { id: value }
                                )}
                                className="text-primary hover:underline"
                            >
                                #{value}
                            </Link>
                        ) : null,
                },
                {
                    key: 'employee_names',
                    title: t('incomes.commissions'),
                    width: 200,
                    headerClassName: 'text-left',
                },
                {
                    key: 'product_group_name',
                    title: t('product_group_name'),
                    width: 180,
                    headerClassName: 'text-left',
                    sortable: true,
                },
                {
                    key: 'service_id',
                    title: t('service_id'),
                    width: 80,
                    headerClassName: 'text-center',
                    cellClassName: 'text-center',
                    render: (value) =>
                        value ? (
                            <Link
                                to={generatePathDatacentral(
                                    paths.serviceDetail,
                                    { id: value }
                                )}
                                className="hover:underline"
                            >
                                {value}
                            </Link>
                        ) : null,
                },
                {
                    key: 'service_name',
                    title: t('service_name'),
                    width: 200,
                    headerClassName: 'text-left',
                    cellClassName: 'truncate',
                    sortable: true,
                    render: (_, row) => (
                        <Link
                            to={generatePathDatacentral(paths.serviceDetail, {
                                id: row.service_id,
                            })}
                            className="text-primary hover:underline"
                        >
                            {row.product_group_name}
                        </Link>
                    ),
                },
                {
                    key: 'service_type',
                    title: t('service_type'),
                    width: 100,
                    headerClassName: 'text-center',
                    cellClassName: 'text-center',
                    render: (value) =>
                        value ? (
                            <span className="uppercase text-xs font-medium">
                                {t(`service_type.${value}`)}
                            </span>
                        ) : null,
                },
                {
                    key: 'month_created_invoice',
                    title: t('month_created_invoice'),
                    width: 120,
                    headerClassName: 'text-center',
                    cellClassName: 'text-center',
                    render: (value) =>
                        value ? dayjs(value).format('MM/YYYY') : '',
                },
                {
                    key: 'invoice_item_amount',
                    title: t('invoice_total_no_vat'),
                    width: 150,
                    headerClassName: 'text-right',
                    cellClassName: 'text-right',
                    sortable: true,
                    render: (value) => formatCurrency(value),
                },
                {
                    key: 'payment_date',
                    title: t('payment_date'),
                    width: 120,
                    headerClassName: 'text-center',
                    cellClassName: 'text-center',
                    sortable: true,
                    render: (value) =>
                        value ? dayjs(value).format('MM/YYYY') : '',
                },
                {
                    key: 'kpi_type',
                    title: t('kpi_type'),
                    width: 120,
                    headerClassName: 'text-center',
                    cellClassName: 'text-center',
                    render: (value) =>
                        value ? (
                            <span className="px-2 py-0.5 bg-blue-100 rounded-full text-xs">
                                {t(`kpi_type.${value}`)}
                            </span>
                        ) : null,
                },
                {
                    key: 'kpi_rate',
                    title: t('kpi_rate'),
                    width: 80,
                    headerClassName: 'text-center',
                    cellClassName: 'text-center',
                    render: (value) =>
                        value !== undefined && value !== null ? (
                            <span className="font-medium">
                                {(value * 100).toFixed(2)}%
                            </span>
                        ) : null,
                },
                {
                    key: 'kpi_amount',
                    title: t('kpi_amount'),
                    width: 150,
                    headerClassName: 'text-right',
                    cellClassName: 'text-right',
                    render: (_, row) => formatCurrency(row.kpi_amount),
                },
            ]),
        [t]
    )

    // Filter items
    const employeeConfig = getEntityConfig('employee')
    const filterItems = useMemo(
        (): FilterItemConfig[] => [
            {
                name: 'sale_manager_codes',
                type: 'async-select',
                label: t('incomes.commissions'),
                placeholder: t('employee_codes'),
                multiple: true,
                cols: 2,
                asyncOptions: (query, page) =>
                    employeeConfig.fetchOptionsWithPage(query, page || 1, {
                        departmentIds: [
                            DEPARTMENT_IDS.SALES,
                            DEPARTMENT_IDS.ACCOUNT,
                            DEPARTMENT_IDS.BOARD_OF_DIRECTORS,
                        ],
                    }),
                getDefaultOptions: (ids) => employeeConfig.fetchByIds(ids),
            },
            {
                name: 'date_range',
                type: 'daterangepicker',
                names: ['gte', 'lte'],
                cols: 2,
                showPresets: true,
            },
        ],
        [t, setFilters]
    )

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        // Data
        incomes: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),
        isFetching,
        isLoading,

        // Stats
        stats: statsData?.result,

        // Form & params
        control,
        setFilter,
        setFilters,
        filters,
        params,
        queryParams,
        searchInputProps,

        // Configs
        columns,
        filterItems,
        EMPLOYEE_INCOME_EXCEL_COLUMNS,

        // Handlers
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        t,
    }
}
